import React, { useState, useEffect } from 'react';
import { X, Check, Copy } from 'lucide-react';
import { Customer, apiService } from '../services/api';

interface EditCustomerModalProps {
    customer: Customer | null;
    isOpen: boolean;
    onClose: () => void;
    onSave: (updatedCustomer: Customer) => void;
}

export const EditCustomerModal: React.FC<EditCustomerModalProps> = ({
    customer,
    isOpen,
    onClose,
    onSave
}) => {
    const [editName, setEditName] = useState('');
    const [editPhone, setEditPhone] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (customer) {
            setEditName(customer.name || '');
            setEditPhone(customer.phoneNumber || '');
        }
    }, [customer]);

    if (!isOpen || !customer) return null;

    const useWhatsappName = () => {
        if (customer.whatsappName) {
            setEditName(customer.whatsappName);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const cleanPhone = editPhone.replace(/\+/g, '');

            const updatedCustomer = await apiService.updateCustomer(customer._id, {
                name: editName,
                phoneNumber: cleanPhone
            });

            onSave(updatedCustomer);
            onClose();
        } catch (error) {
            console.error('Failed to update customer:', error);
            alert('Failed to update customer. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        setEditName(customer.name || '');
        setEditPhone(customer.phoneNumber || '');
        onClose();
    };

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Edit Customer</h3>
                    <button className="ghost-button" onClick={handleClose}>
                        <X size={18} />
                    </button>
                </div>

                <div className="modal-body">
                    {/* Read-only WhatsApp Info Section */}
                    {/* <div className="readonly-info-card form-group">
                        <label>WhatsApp ID</label>
                        <div className="value">{customer.whatsappId || '—'}</div>
                    </div> */}

                    {/* <div className="readonly-info-card form-group">
                        <label>WhatsApp Name (from contact)</label>
                        <div className={`value ${!customer.whatsappName ? 'muted' : ''}`}>
                            {customer.whatsappName || 'Not available'}
                        </div>
                    </div> */}

                    {/* <div className="form-section-divider">Editable Fields</div> */}

                    {/* Editable Lead Name */}
                    <div className="form-group">
                        <label>Lead Name</label>
                        <div className="input-with-action">
                            <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                placeholder="Enter lead name"
                                className="input-field"
                                autoFocus
                            />
                            {customer.whatsappName && (
                                <button
                                    className="ghost-button"
                                    onClick={useWhatsappName}
                                    title="Copy WhatsApp Name to Lead Name"
                                >
                                    <Copy size={14} /> Use WA Name
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Editable Phone Number */}
                    <div className="form-group">
                        <label>Phone Number</label>
                        <input
                            type="text"
                            value={editPhone}
                            onChange={(e) => setEditPhone(e.target.value.replace(/[^0-9]/g, ''))}
                            placeholder="e.g. 201234567890 (digits only)"
                            className="input-field"
                        />
                        <p className="form-hint">Enter digits only, without country code prefix (+)</p>
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="ghost-button" onClick={handleClose} disabled={saving}>
                        Cancel
                    </button>
                    <button className="primary-button" onClick={handleSave} disabled={saving}>
                        {saving ? (
                            <>
                                <span className="spinner small" style={{ width: 16, height: 16 }}></span>
                                Saving...
                            </>
                        ) : (
                            <>
                                Save Changes
                                <Check size={16} />
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
